import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ImagePlus, Plus, RotateCcw } from 'lucide-react'
import { useState, type ReactElement } from 'react'

import { Button } from '@krp/ui-kit'

import '../../../shared/platform.css'
import type { ScanEventKind, ScanSessionVm } from '../model/types'
import './scanSession.css'

const KIND_ICON: Readonly<Record<ScanEventKind, ReactElement>> = {
  created: <Plus size={14} aria-hidden="true" />,
  attached: <ImagePlus size={14} aria-hidden="true" />,
  found: <CheckCircle2 size={14} aria-hidden="true" />,
  skipped: <RotateCcw size={14} aria-hidden="true" />,
  failed: <AlertTriangle size={14} aria-hidden="true" />,
}

const pad = (value: number): string => String(value).padStart(2, '0')

function timeOf(at: number): string {
  const date = new Date(at)
  return Number.isNaN(date.getTime()) ? '' : `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Смена сканирования в шапке сканера (RP-FB56): «Создано 3 · Прикреплено 1 · Найдено 5 · Пропущено 1» — словами, не одними цифрами.
 * Нажатие раскрывает ленту: что сделано, когда, и у созданного/найденного — «Открыть». Под счётчиком — последнее событие
 * («Фото прикреплено к «Молоко»»): после возврата из окна человек видит, чем кончилось. Это счётчик смены, а не журнал.
 */
export function ScanSessionBar({
  vm,
  surface,
  onOpenProduct,
}: {
  vm: ScanSessionVm
  /** Где стоит: поверх камеры (светлый текст) или на странице. */
  surface: 'camera' | 'page'
  /** Открыть общий товар из ленты; не задано — «Открыть» нет. */
  onOpenProduct?: ((productId: string) => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="platform-scan-session" data-surface={surface} role="group" aria-label="Смена сканирования">
      <Button
        variant="ghost"
        icon={open ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        actionId="platform.scan.session-toggle"
      >
        {vm.summary}
      </Button>
      {vm.last !== null && !open && (
        <p className="platform-scan-session__last" role="status">
          {KIND_ICON[vm.last.kind]}
          {vm.last.text}
        </p>
      )}
      {open && (
        <div className="platform-scan-session__log">
          {vm.entries.length === 0 ? (
            <p className="platform-scan-session__empty">Пока ничего не сделано.</p>
          ) : (
            <ul aria-label="Лента смены">
              {vm.entries.map((entry) => (
                <li key={entry.id} data-kind={entry.kind}>
                  {KIND_ICON[entry.kind]}
                  <span className="platform-scan-session__text">{entry.text}</span>
                  <span className="platform-scan-session__time">{timeOf(entry.at)}</span>
                  {entry.productId !== null && entry.kind !== 'failed' && onOpenProduct !== undefined && (
                    <Button variant="ghost" onClick={() => onOpenProduct(entry.productId ?? '')} actionId="platform.scan.session-open">
                      Открыть
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
